import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faMagnifyingGlass, faGlobe, faLayerGroup, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const normalizeText = (value) => String(value || '').trim()

const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const CountryAnalysis = () => {
  const db = getFirestore(app)
  const [tableData, setTableData] = useState([])
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const snapshot = await getDocs(collection(db, 'users'))
      const grouped = {}
      const courseTotals = {}

      snapshot.forEach((docItem) => {
        const user = docItem.data()
        if (normalizeStatus(user.userType) !== 'student') return

        const nationality = normalizeText(user.nationality) || 'Unknown'
        const course = normalizeText(user.course) || 'Unspecified'

        if (!grouped[nationality]) {
          grouped[nationality] = { nationality, total: 0 }
        }

        grouped[nationality][course] = (grouped[nationality][course] || 0) + 1
        grouped[nationality].total += 1

        courseTotals[course] = (courseTotals[course] || 0) + 1
      })

      const courseList = Object.keys(courseTotals).sort((first, second) => {
        if (courseTotals[second] !== courseTotals[first]) {
          return courseTotals[second] - courseTotals[first]
        }

        return first.localeCompare(second)
      })

      const dataArray = Object.values(grouped).sort((first, second) => {
        if (second.total !== first.total) {
          return second.total - first.total
        }

        return first.nationality.localeCompare(second.nationality)
      })

      setCourses(courseList)
      setTableData(dataArray)
    } catch (fetchError) {
      console.error('CountryAnalysis fetch error', fetchError)
      setTableData([])
      setCourses([])
      setError('We could not load the nationality analysis right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return tableData
    }

    return tableData.filter((row) => row.nationality.toLowerCase().includes(search))
  }, [searchTerm, tableData])

  const stats = useMemo(() => {
    const totals = {
      students: 0,
      nationalities: tableData.length,
      courses: courses.length,
      topNationality: tableData[0] || null,
      topCourse: null
    }

    const courseBreakdown = {}

    tableData.forEach((row) => {
      totals.students += row.total

      courses.forEach((course) => {
        courseBreakdown[course] = (courseBreakdown[course] || 0) + (row[course] || 0)
      })
    })

    const topCourseName = Object.keys(courseBreakdown).sort((first, second) => {
      if (courseBreakdown[second] !== courseBreakdown[first]) {
        return courseBreakdown[second] - courseBreakdown[first]
      }

      return first.localeCompare(second)
    })[0]

    if (topCourseName) {
      totals.topCourse = {
        name: topCourseName,
        total: courseBreakdown[topCourseName]
      }
    }

    return totals
  }, [courses, tableData])

  const courseTotals = useMemo(() => {
    return courses.reduce((accumulator, course) => {
      accumulator[course] = tableData.reduce((sum, row) => sum + (row[course] || 0), 0)
      return accumulator
    }, {})
  }, [courses, tableData])

  const topNationalities = filteredRows.slice(0, 5)
  const topCourses = courses.slice(0, 5)

  const matrixMax = useMemo(() => {
    let max = 0

    tableData.forEach((row) => {
      courses.forEach((course) => {
        max = Math.max(max, row[course] || 0)
      })
    })

    return Math.max(max, 1)
  }, [courses, tableData])

  const hasRows = filteredRows.length > 0
  const emptyMessage = tableData.length
    ? 'No nationality matches the current search.'
    : 'No student nationality data was found yet.'

  return (
    <div className="mainContainer countryAnalysisPage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="countryAnalysisContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Nationality vs Course Choice</h4>
              <p className="appsSubtext">
                Explore how student nationalities are distributed across courses, with a quick view of the strongest concentrations.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Tracked students</span>
                <strong>{formatCount(stats.students)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Nationalities</span>
                <strong>{formatCount(stats.nationalities)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Courses</span>
                <strong>{formatCount(stats.courses)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Top course</span>
                <strong>{stats.topCourse ? stats.topCourse.name : '—'}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard countryAnalysisFiltersCard">
            <div className="appsSearch input-group countryAnalysisSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search nationality..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="countryAnalysisFiltersMeta">
              <span>{formatCount(filteredRows.length)} nationalities visible</span>
              <span>{formatCount(courses.length)} course columns in the matrix</span>
            </div>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="countryAnalysisGrid">
            <div className="appsTableCard countryAnalysisTableCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                  Nationality and course matrix
                </h6>
                <span>{formatCount(filteredRows.length)} results</span>
              </div>
              <div className="countryAnalysisTableWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading country analysis...</span>
                    </Spinner>
                    <p>Loading nationality analysis...</p>
                  </div>
                ) : (
                  <Table hover responsive className="appsTable countryAnalysisTable">
                    <thead>
                      <tr>
                        <th>Nationality</th>
                        {courses.map((course) => (
                          <th key={course}>{course}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hasRows ? (
                        filteredRows.map((row) => (
                          <tr key={row.nationality}>
                            <td>
                              <div className="countryAnalysisRowLabel">
                                <strong>{row.nationality}</strong>
                                <span>{formatCount(row.total)} students</span>
                              </div>
                            </td>
                            {courses.map((course) => {
                              const count = row[course] || 0
                              const heat = Math.max(count / matrixMax, count > 0 ? 0.08 : 0)

                              return (
                                <td key={course}>
                                  <div
                                    className={`countryAnalysisCountPill${count > 0 ? ' countryAnalysisCountPill--filled' : ''}`}
                                    style={{ '--country-heat': heat }}
                                  >
                                    {formatCount(count)}
                                  </div>
                                </td>
                              )
                            })}
                            <td>
                              <div className="countryAnalysisTotalCell">{formatCount(row.total)}</div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={courses.length + 2} className="appsEmptyState">
                            {emptyMessage}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>

            <div className="appsInsightsCard countryAnalysisInsightsCard">
              <div className="appsInsightsHeader">
                <h6>
                  <FontAwesomeIcon icon={faGlobe} className="me-2" />
                  Leading nationalities
                </h6>
                <span>{formatCount(topNationalities.length)} shown</span>
              </div>

              <div className="appsInsightsList">
                {topNationalities.length ? (
                  topNationalities.map((row) => (
                    <div className="appsInsightsItem" key={row.nationality}>
                      <div>
                        <strong>{row.nationality}</strong>
                        <span>{formatCount(row.total)} students</span>
                      </div>
                      <small>
                        {courses[0] ? `${formatCount(row[courses[0]] || 0)} in ${courses[0]}` : 'Top nationality'}
                      </small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <FontAwesomeIcon icon={faLayerGroup} size="2x" className="mb-1 text-secondary" />
                    <p>No nationality insights match the current search.</p>
                  </div>
                )}
              </div>

              <div className="countryAnalysisCourseBlock">
                <div className="appsInsightsHeader">
                  <h6>Most common courses</h6>
                  <span>All students</span>
                </div>

                <div className="countryAnalysisCourseList">
                  {topCourses.length ? (
                    topCourses.map((course) => (
                      <div className="countryAnalysisCourseItem" key={course}>
                        <div>
                          <strong>{course}</strong>
                          <span>{formatCount(courseTotals[course] || 0)} students</span>
                        </div>
                        <small>{formatCount(courseTotals[course] || 0)}</small>
                      </div>
                    ))
                  ) : (
                    <div className="countryAnalysisCourseEmpty">No course data yet.</div>
                  )}
                </div>
              </div>

              <p className="appsInsightsMore">
                Search a nationality to focus the matrix, or refresh to re-read the latest student records.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CountryAnalysis
